import React, { useState } from 'react';
import './VisaEligibilityQuiz.css';

/**
 * VisaEligibilityQuiz Component
 * 
 * A multi-step pre-application readiness check. It is not a legal eligibility
 * determination and cannot predict an immigration decision.
 * 
 * Target audience: Indian visa applicants
 * 
 * @param {Function} onUpgrade - Callback function to trigger upgrade flow for premium features
 */
const VisaEligibilityQuiz = ({ onUpgrade, hasPremiumAccess = false }) => {
  // Current step in the wizard (1-6)
  const [step, setStep] = useState(1);
  
  // User's answers to each question
  const [answers, setAnswers] = useState({
    purpose: '',           // Study, Work, Tourism, Family, Business
    ageGroup: '',          // Under 18, 18-35, 36-50, 50+
    education: '',         // 10th, 12th, Graduate, Postgraduate, Other
    funds: '',             // Yes, No, Not Sure
    previousRefusal: '',   // Never, Ireland, Other country, Both
    jobOffer: ''           // Yes, No, In Process
  });
  
  // Calculated eligibility result
  const [result, setResult] = useState(null);

  // Question definitions with options and scoring weights
  const questions = {
    1: {
      id: 'purpose',
      question: 'What is your purpose of visit to Ireland?',
      icon: '🎯',
      options: [
        { value: 'study', label: 'Study / Education', icon: '📚' },
        { value: 'work', label: 'Work / Employment', icon: '💼' },
        { value: 'tourism', label: 'Tourism / Visit', icon: '✈️' },
        { value: 'family', label: 'Family Reunification', icon: '👨‍👩‍👧‍👦' },
        { value: 'business', label: 'Business / Investment', icon: '🏢' }
      ]
    },
    2: {
      id: 'ageGroup',
      question: 'What is your age group?',
      icon: '👤',
      options: [
        { value: 'under18', label: 'Under 18 years' },
        { value: '18-35', label: '18 - 35 years' },
        { value: '36-50', label: '36 - 50 years' },
        { value: '50plus', label: '50+ years' }
      ]
    },
    3: {
      id: 'education',
      question: 'What is your highest educational qualification?',
      icon: '🎓',
      options: [
        { value: '10th', label: '10th Pass / Secondary' },
        { value: '12th', label: '12th Pass / Higher Secondary' },
        { value: 'graduate', label: 'Graduate / Bachelor\'s Degree' },
        { value: 'postgraduate', label: 'Postgraduate / Master\'s Degree' },
        { value: 'other', label: 'Other / Diploma / Certification' }
      ]
    },
    4: {
      id: 'funds',
      question: 'Do you have sufficient funds to support your stay in Ireland?',
      icon: '💰',
      options: [
        { value: 'yes', label: 'Yes, I have documented proof of funds' },
        { value: 'no', label: 'No, I need financial assistance' },
        { value: 'unsure', label: 'Not Sure / Need guidance on requirements' }
      ]
    },
    5: {
      id: 'previousRefusal',
      question: 'Have you ever been refused a visa before?',
      icon: '📋',
      options: [
        { value: 'never', label: 'Never refused any visa' },
        { value: 'ireland', label: 'Refused by Ireland only' },
        { value: 'other', label: 'Refused by another country' },
        { value: 'both', label: 'Refused by both Ireland and other countries' }
      ]
    },
    6: {
      id: 'jobOffer',
      question: 'Do you have a job offer or University admission letter?',
      icon: '📄',
      options: [
        { value: 'yes', label: 'Yes, I have a confirmed offer/admission' },
        { value: 'inprocess', label: 'In Process / Application pending' },
        { value: 'no', label: 'No, I don\'t have one yet' }
      ]
    }
  };

  /**
   * Calculate eligibility score and determine status
   * Score is based on weighted criteria for each answer
   */
  const calculateEligibility = (finalAnswers) => {
    let score = 0;
    const missingRequirements = [];
    const recommendedVisas = [];
    const nextSteps = [];

    // Purpose scoring (max 25 points)
    const purposeScores = {
      'study': { score: 25, visas: ['Study Visa (Type D)'] },
      'work': { score: 20, visas: ['Critical Skills Employment Permit', 'General Employment Permit'] },
      'tourism': { score: 22, visas: ['Tourist Visa (Type C)'] },
      'family': { score: 18, visas: ['Family Reunification Visa'] },
      'business': { score: 20, visas: ['Business Visa', 'Start-up Entrepreneur Programme'] }
    };

    if (finalAnswers.purpose && purposeScores[finalAnswers.purpose]) {
      score += purposeScores[finalAnswers.purpose].score;
      recommendedVisas.push(...purposeScores[finalAnswers.purpose].visas);
    }

    // Age group scoring (max 15 points)
    const ageScores = {
      'under18': { score: 10, note: 'Minor application requires guardian consent' },
      '18-35': { score: 15, note: 'Prime age for study/work visas' },
      '36-50': { score: 12, note: 'Good for experienced professionals' },
      '50plus': { score: 8, note: 'May require additional documentation' }
    };

    if (finalAnswers.ageGroup && ageScores[finalAnswers.ageGroup]) {
      score += ageScores[finalAnswers.ageGroup].score;
    }

    // Education scoring (max 20 points)
    const educationScores = {
      '10th': { score: 5, note: 'May limit visa options' },
      '12th': { score: 10, note: 'Eligible for basic visa types' },
      'graduate': { score: 18, note: 'Good eligibility for most visas' },
      'postgraduate': { score: 20, note: 'Excellent for Critical Skills permit' },
      'other': { score: 12, note: 'Depends on certification type' }
    };

    if (finalAnswers.education && educationScores[finalAnswers.education]) {
      score += educationScores[finalAnswers.education].score;
    } else {
      missingRequirements.push('Educational qualification assessment needed');
    }

    // Funds scoring (max 20 points)
    if (finalAnswers.funds === 'yes') {
      score += 20;
    } else if (finalAnswers.funds === 'unsure') {
      score += 10;
      missingRequirements.push('Proof of sufficient funds documentation');
      nextSteps.push('Check financial requirements for your visa type');
    } else {
      missingRequirements.push('Proof of sufficient funds (critical requirement)');
      nextSteps.push('Arrange financial documentation before applying');
    }

    // Previous refusal scoring (max 10 points)
    const refusalScores = {
      'never': { score: 10, note: 'Clean visa history' },
      'ireland': { score: 0, note: 'Previous Ireland refusal requires explanation letter' },
      'other': { score: 5, note: 'May need to declare other country refusals' },
      'both': { score: 0, note: 'Complex case - consider professional help' }
    };

    if (finalAnswers.previousRefusal && refusalScores[finalAnswers.previousRefusal]) {
      score += refusalScores[finalAnswers.previousRefusal].score;
      if (finalAnswers.previousRefusal !== 'never') {
        missingRequirements.push('Explanation letter for previous refusal(s)');
        nextSteps.push('Prepare detailed explanation for previous visa refusal');
      }
    }

    // Job offer/admission scoring (max 10 points)
    if (finalAnswers.jobOffer === 'yes') {
      score += 10;
    } else if (finalAnswers.jobOffer === 'inprocess') {
      score += 5;
      missingRequirements.push('Confirmed job offer or admission letter');
      nextSteps.push('Follow up on pending applications');
    } else {
      if (finalAnswers.purpose === 'study' || finalAnswers.purpose === 'work') {
        missingRequirements.push('Job offer or University admission (required for your visa type)');
        nextSteps.push('Secure admission/offer before applying for visa');
      } else {
        nextSteps.push('Gather all required documents for your visa type');
      }
    }

    // Calculate percentage (max score = 100)
    const percentage = Math.min(100, Math.max(0, score));

    // Determine eligibility status
    let status, statusColor, statusIcon, statusMessage;

    if (percentage >= 80) {
      status = 'Higher readiness';
      statusColor = 'status-eligible';
      statusIcon = '✅';
      statusMessage = 'Your answers match the basic checklist used by this tool. Confirm the current visa requirements before applying.';
    } else if (percentage >= 60) {
      status = 'Needs preparation';
      statusColor = 'status-partial';
      statusIcon = '👍';
      statusMessage = 'Your answers match many checklist items. Address the missing items and confirm the current visa requirements.';
    } else if (percentage >= 40) {
      status = 'Needs review';
      statusColor = 'status-review';
      statusIcon = '⚠️';
      statusMessage = 'Your profile needs improvement. Consider consulting an immigration advisor.';
    } else {
      status = 'Low readiness';
      statusColor = 'status-not-eligible';
      statusIcon = '❌';
      statusMessage = 'You may face significant challenges. Professional guidance is strongly recommended.';
    }

    // Add general next steps
    if (nextSteps.length === 0) {
      nextSteps.push('Gather all required documents');
      nextSteps.push('Submit your application through the AVATS system');
      nextSteps.push('Prepare for biometrics appointment');
    }

    return {
      percentage,
      status,
      statusColor,
      statusIcon,
      statusMessage,
      recommendedVisas: [...new Set(recommendedVisas)], // Remove duplicates
      missingRequirements,
      nextSteps
    };
  };

  /**
   * Handle answer selection and progress to next step
   */
  const handleAnswer = (questionId, optionValue) => {
    const question = questions[questionId];
    const updatedAnswers = { ...answers, [question.id]: optionValue };
    setAnswers(updatedAnswers);

    if (questionId < 6) {
      setStep(questionId + 1);
    } else {
      // Calculate and show results
      const eligibilityResult = calculateEligibility(updatedAnswers);
      setResult(eligibilityResult);
    }
  };

  /**
   * Reset the quiz to start over
   */
  const resetQuiz = () => {
    setStep(1);
    setAnswers({
      purpose: '',
      ageGroup: '',
      education: '',
      funds: '',
      previousRefusal: '',
      jobOffer: ''
    });
    setResult(null);
  };

  /**
   * Share results - copy to clipboard or open share dialog
   */
  const handleShare = async () => {
    const shareText = `I just checked my Ireland visa eligibility on IrelandVisaTracker.in!\n\n` +
      `📊 Eligibility Score: ${result.percentage}%\n` +
      `✅ Status: ${result.status}\n\n` +
      `Check your eligibility: https://irelandvisatracker.in`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Ireland Visa Eligibility',
          text: shareText,
          url: 'https://irelandvisatracker.in'
        });
      } catch (err) {
        // User cancelled or share failed - fall back to clipboard
        copyToClipboard(shareText);
      }
    } else {
      copyToClipboard(shareText);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Results copied to clipboard!');
    }).catch(() => {
      alert('Unable to copy. Please share manually.');
    });
  };

  // Render results view
  if (result) {
    return (
      <div className="eligibility-quiz">
        <div className="quiz-header">
          <h2>📊 Your Visa Readiness Results</h2>
          <button onClick={resetQuiz} className="reset-quiz-btn">
            🔄 Retake Quiz
          </button>
        </div>

        <div className="result-section">
          {/* Eligibility Score Circle */}
          <div className={`eligibility-circle ${result.statusColor}`}>
            <div className="eligibility-value">{result.percentage}%</div>
            <div className="eligibility-label">{result.status}</div>
          </div>

          {/* Status Message */}
          <div className="status-message">
            <span className="status-icon">{result.statusIcon}</span>
            <p>{result.statusMessage} This is an educational checklist score, not a visa approval or legal eligibility decision.</p>
          </div>

          {/* Recommended Visa Types */}
          {result.recommendedVisas.length > 0 && (
            <div className="result-card">
              <h3>📋 Recommended Visa Types</h3>
              <div className="visa-tags">
                {result.recommendedVisas.map((visa, index) => (
                  <span key={index} className="visa-tag">{visa}</span>
                ))}
              </div>
            </div>
          )}

          {/* Missing Requirements */}
          {result.missingRequirements.length > 0 && (
            <div className="result-card requirements-card">
              <h3>⚠️ Missing Requirements</h3>
              <ul className="requirements-list">
                {result.missingRequirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Next Steps */}
          <div className="result-card next-steps-card">
            <h3>🎯 Recommended Next Steps</h3>
            <ol className="next-steps-list">
              {result.nextSteps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>

          {/* Share Results */}
          <div className="share-section">
            <button className="share-btn" onClick={handleShare}>
              📤 Share Your Results
            </button>
          </div>

          {/* Premium CTA */}
          <div className="premium-cta">
            <div className="cta-icon">⭐</div>
            <div className="cta-content">
              <h4>Want a Detailed Eligibility Analysis?</h4>
              <p>
                Get instant email + web push alerts and unlock all 13 premium tools,
                including personalized eligibility guidance for your profile.
              </p>
              <ul className="premium-features">
                <li>✅ Instant email + web push alerts</li>
                <li>✅ Full access to all 13 premium tools</li>
                <li>✅ Personalized document checklist and guidance</li>
                <li>✅ Priority support</li>
              </ul>
            </div>
            {!hasPremiumAccess && (
              <button className="btn-primary btn-shimmer cta-button" onClick={onUpgrade}>
                Upgrade for ₹349 – Alerts + 13 Tools →
              </button>
            )}
            <p className="cta-footnote">
              🔒 Secure payment • Instant activation
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render quiz view
  const currentQuestion = questions[step];

  return (
    <div className="eligibility-quiz">
      <div className="quiz-header">
        <h2>🛂 Am I Eligible for an Ireland Visa?</h2>
        <p className="quiz-subtitle">
          Answer 6 quick questions to check your visa eligibility
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="progress-indicator">
        <div className="progress-steps">
          {[1, 2, 3, 4, 5, 6].map(num => (
            <div 
              key={num} 
              className={`progress-step ${step >= num ? 'active' : ''} ${step > num ? 'completed' : ''}`}
            >
              <div className="step-circle">
                {step > num ? '✓' : num}
              </div>
            </div>
          ))}
        </div>
        <div className="progress-bar-container">
          <div 
            className="eligibility-progress-bar"
            style={{ width: `${(step / 6) * 100}%` }}
          ></div>
        </div>
        <p className="eligibility-progress-text">Question {step} of 6</p>
      </div>

      {/* Question Section */}
      <div className="question-section">
        <div className="question-header">
          <span className="question-icon">{currentQuestion.icon}</span>
          <h3 className="eligibility-question-text">{currentQuestion.question}</h3>
        </div>
        <div className="options-grid">
          {currentQuestion.options.map((option) => (
            <button
              key={option.value}
              className={`option-button ${option.icon ? 'has-icon' : ''}`}
              onClick={() => handleAnswer(step, option.value)}
            >
              {option.icon && <span className="option-icon">{option.icon}</span>}
              <span className="option-label">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Back Button */}
      {step > 1 && (
        <button className="back-button" onClick={() => setStep(step - 1)}>
          ← Back
        </button>
      )}
    </div>
  );
};

export default VisaEligibilityQuiz;
